import { createContext, createSignal, onCleanup, onMount, useContext, type JSX } from "solid-js";
import type { Accessor } from "solid-js";
import { onAuthStateChanged, signOut, type User as FirebaseUser } from "firebase/auth";
import { firebase } from "@core/firebase/app";
import { userService } from "@core/services/providers/UserService";
import { chatService } from "@core/services/providers/ChatService";
import { notificationService } from "@core/services/NotificationService";
import type { ChatMessage, ChatThread } from "@core/services/models/Chat";
import type { UserProfile, UserProfileDoc } from "@core/services/models/User";

export type AuthStatus = "loading" | "signed-out" | "verifying" | "signed-in" | "error";

export type ToastMessage = {
  id: string;
  type: "success" | "error" | "info";
  message: string;
  duration?: number;
};

type ClientUser = {
  uid: string;
  phoneNumber?: string | null;
  displayName?: string | null;
  email?: string | null;
};

type AppStateSnapshot = {
  authStatus: Accessor<AuthStatus>;
  currentUser: Accessor<ClientUser | null>;
  profile: Accessor<UserProfile | null>;
  activeChat: Accessor<ChatThread | null>;
  messages: Accessor<ChatMessage[]>;
  isOffline: Accessor<boolean>;
  isInitializingChat: Accessor<boolean>;
  isSendingMessage: Accessor<boolean>;
  composerDraft: Accessor<string>;
  toast: Accessor<ToastMessage | null>;
};

class AppStateService {
  private initialized = false;
  private firebaseUserSignal = createSignal<ClientUser | null>(null);
  private authStatusSignal = createSignal<AuthStatus>("loading");
  private profileSignal = createSignal<UserProfile | null>(null);
  private activeChatSignal = createSignal<ChatThread | null>(null);
  private messagesSignal = createSignal<ChatMessage[]>([]);
  private isInitializingChatSignal = createSignal<boolean>(false);
  private isSendingSignal = createSignal<boolean>(false);
  private composerDraftSignal = createSignal<string>("");
  private toastSignal = createSignal<ToastMessage | null>(null);
  private isOfflineSignal = createSignal<boolean>(typeof navigator !== "undefined" ? !navigator.onLine : false);

  private authUnsubscribe?: () => void;
  private profileUnsubscribe?: () => void;
  private chatUnsubscribe?: () => void;
  private messagesUnsubscribe?: () => void;
  private onlineListener?: () => void;
  private offlineListener?: () => void;
  private previousMessageCount = 0;

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.attachNetworkListeners();
    this.attachAuthListener();
  }

  snapshot(): AppStateSnapshot {
    return {
      authStatus: this.authStatusSignal[0],
      currentUser: this.firebaseUserSignal[0],
      profile: this.profileSignal[0],
      activeChat: this.activeChatSignal[0],
      messages: this.messagesSignal[0],
      isOffline: this.isOfflineSignal[0],
      isInitializingChat: this.isInitializingChatSignal[0],
      isSendingMessage: this.isSendingSignal[0],
      composerDraft: this.composerDraftSignal[0],
      toast: this.toastSignal[0]
    };
  }

  setComposerDraft(value: string): void {
    this.composerDraftSignal[1](value);
  }

  showToast(toast: ToastMessage): void {
    this.toastSignal[1](toast);
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        if (this.toastSignal[0]() === toast) {
          this.toastSignal[1](null);
        }
      }, toast.duration);
    }
  }

  clearToast(): void {
    this.toastSignal[1](null);
  }

  setAuthStatus(status: AuthStatus): void {
    this.authStatusSignal[1](status);
  }

  async sendMessage(text: string): Promise<void> {
    if (this.isSendingSignal[0]()) {
      return;
    }
    const user = this.firebaseUserSignal[0]();
    const chat = this.activeChatSignal[0]();
    const trimmed = text.trim();
    if (!user || !chat || !trimmed) {
      return;
    }
    this.isSendingSignal[1](true);
    this.setComposerDraft("");
    try {
      await chatService.sendMessage(chat.id, trimmed, user.uid);
    } catch (error) {
      console.error("Failed to send message", error);
      this.showToast({ id: "send-error", type: "error", message: "Message failed to send", duration: 4000 });
      this.setComposerDraft(trimmed);
    } finally {
      this.isSendingSignal[1](false);
    }
  }

  async signOut(): Promise<void> {
    await signOut(firebase.auth);
  }

  async updateProfile(updates: Partial<UserProfileDoc>): Promise<void> {
    const user = this.firebaseUserSignal[0]();
    if (!user) {
      return;
    }
    try {
      await userService.updateProfile(user.uid, updates);
      this.showToast({ id: "profile-updated", type: "success", message: "Settings saved", duration: 2400 });
    } catch (error) {
      console.error("Failed to update profile", error);
      this.showToast({ id: "profile-error", type: "error", message: "Unable to save settings", duration: 4000 });
    }
  }

  /**
   * Fire a browser notification for any event
   * Components can call this through useAppState()
   */
  async fireNotification(title: string, body?: string, options?: { tag?: string; data?: any }): Promise<void> {
    await notificationService.showNotification({
      title,
      body,
      tag: options?.tag,
      data: options?.data
    });
  }

  dispose(): void {
    this.teardownSubscriptions();
    this.authUnsubscribe?.();
    this.authUnsubscribe = undefined;
    if (typeof window !== "undefined") {
      if (this.onlineListener) {
        window.removeEventListener("online", this.onlineListener);
      }
      if (this.offlineListener) {
        window.removeEventListener("offline", this.offlineListener);
      }
    }
  }

  private attachNetworkListeners(): void {
    if (typeof window === "undefined") {
      return;
    }
    this.onlineListener = () => this.isOfflineSignal[1](false);
    this.offlineListener = () => this.isOfflineSignal[1](true);
    window.addEventListener("online", this.onlineListener);
    window.addEventListener("offline", this.offlineListener);
  }

  private attachAuthListener(): void {
    if (this.authUnsubscribe) {
      return;
    }
    this.authUnsubscribe = onAuthStateChanged(firebase.auth, async (user) => {
      if (!user) {
        this.handleSignedOut();
        return;
      }
      await this.handleSignedIn(user);
    });
  }

  private async handleSignedIn(user: FirebaseUser): Promise<void> {
    const clientUser: ClientUser = {
      uid: user.uid,
      phoneNumber: user.phoneNumber,
      displayName: user.displayName,
      email: user.email
    };

    this.firebaseUserSignal[1](clientUser);
    this.authStatusSignal[1]("signed-in");

    await userService.ensureProfile(user.uid, {
      phoneNumber: user.phoneNumber ?? undefined,
      displayName: user.displayName ?? undefined,
      email: user.email ?? undefined
    });

    this.subscribeToProfile(user.uid);
    await this.ensureChatForUser(user.uid);
  }

  private handleSignedOut(): void {
    this.firebaseUserSignal[1](null);
    this.authStatusSignal[1]("signed-out");
    this.profileSignal[1](null);
    this.activeChatSignal[1](null);
    this.messagesSignal[1]([]);
    this.composerDraftSignal[1]("");
    this.isSendingSignal[1](false);
    this.previousMessageCount = 0;
    this.teardownSubscriptions();
  }

  private subscribeToProfile(userId: string): void {
    this.profileUnsubscribe?.();
    this.profileUnsubscribe = userService.subscribeToProfile(userId, (profile) => {
      this.profileSignal[1](profile);
    });
  }

  private async ensureChatForUser(userId: string): Promise<void> {
    try {
      this.isInitializingChatSignal[1](true);
      const thread = await chatService.ensureActiveChatForUser(userId);
      this.activeChatSignal[1](thread);
      this.subscribeToChat(thread.id);
      this.subscribeToMessages(thread.id);
    } catch (error) {
      console.error("Failed to ensure chat thread", error);
      this.showToast({ id: "chat-error", type: "error", message: "Unable to load chat", duration: 4000 });
    } finally {
      this.isInitializingChatSignal[1](false);
    }
  }

  private subscribeToChat(chatId: string): void {
    this.chatUnsubscribe?.();
    this.chatUnsubscribe = chatService.subscribeToChat(chatId, (thread) => {
      this.activeChatSignal[1](thread);
    });
  }

  private subscribeToMessages(chatId: string): void {
    this.messagesUnsubscribe?.();
    this.messagesUnsubscribe = chatService.subscribeToMessages(chatId, (messages) => {
      const currentUser = this.firebaseUserSignal[0]();

      // Detect new messages from others (not sent by current user)
      if (this.previousMessageCount > 0 && messages.length > this.previousMessageCount && currentUser) {
        const newMessages = messages.slice(this.previousMessageCount);

        for (const msg of newMessages) {
          // Only notify for messages not from the current user
          if (msg.senderId !== currentUser.uid) {
            const senderName = msg.senderType === "admin" ? "HappyTidy Support" : "HappyTidy";
            notificationService.notifyNewMessage(senderName, msg.text, msg.id);
          }
        }
      }

      this.previousMessageCount = messages.length;
      this.messagesSignal[1](messages);
    });
  }

  private teardownSubscriptions(): void {
    this.profileUnsubscribe?.();
    this.profileUnsubscribe = undefined;
    this.chatUnsubscribe?.();
    this.chatUnsubscribe = undefined;
    this.messagesUnsubscribe?.();
    this.messagesUnsubscribe = undefined;
  }
}

const service = new AppStateService();

const AppStateContext = createContext<AppStateService>(service);

export const AppStateProvider = (props: { children: JSX.Element }) => {
  onMount(() => {
    service.initialize();
  });
  onCleanup(() => {
    service.dispose();
  });
  return <AppStateContext.Provider value={service}>{props.children}</AppStateContext.Provider>;
};

export const useAppState = (): AppStateService => {
  const ctx = useContext(AppStateContext);
  if (!ctx) {
    throw new Error("AppStateContext is not available");
  }
  return ctx;
};

export type { AppStateService };

